import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GetAllJobList } from "../../../Redux/action/JobDetailsAction";
import { useLocation, useNavigate, Link } from "react-router-dom";

import {
  Box,
  Card,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Pagination from "../../Common/Pagination/Pagination";
import useDebounce from "../../../Hooks/useDebounce";

const JobPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(0);
  const [size] = useState(8);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const location = useLocation();
  const currentTab = new URLSearchParams(location.search)
    .get("active")
    ?.toUpperCase();
  const [activeTab, setActiveTab] = useState(currentTab ?? "ALL");

  const loader = useSelector((state) => state?.JobPageDashboard?.loading);
  const JobDetails = useSelector(
    (state) => state?.JobPageDashboard?.result?.jobs,
  );
  const totalPages = useSelector(
    (state) => state?.JobPageDashboard?.result?.totalPages,
  );

  useEffect(() => {
    dispatch(
      GetAllJobList({ pageNumber, size, search: debouncedSearch, activeTab }),
    );
  }, [pageNumber, debouncedSearch, activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPageNumber(0);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F4F7FE",
        minHeight: "100vh",
        p: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          background: `
          linear-gradient(
          180deg,
          rgba(45, 109, 150, 0.08) 0%,
          rgba(45, 109, 150, 0.02) 60%,
          #ffffff 100%
          )
        `,
          border: "1px solid rgba(45, 109, 150, 0.2)",
          position: "sticky",
          top: 0,
          zIndex: 10,
          backdropFilter: "blur(6px)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Search */}
          <Box
            sx={{
              width: { xs: "100%", md: 280 },
              order: { xs: 1, md: 2 },
            }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder="Search by company name"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPageNumber(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#2d6d96" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "rgba(45, 109, 150, 0.35)" },
                  "&:hover fieldset": { borderColor: "#2d6d96" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2d6d96",
                    borderWidth: 2,
                  },
                },
              }}
            />
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="standard"
            scrollButtons={false}
            sx={{
              order: { xs: 2, md: 1 },
              "& .MuiTabs-flexContainer": {
                flexWrap: "wrap",
              },
              "& .MuiTab-root": {
                transition: "background-color 0.2s ease, color 0.2s ease",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            {[
              { label: "All Jobs", value: "ALL", link: "all" },
              { label: "Posted Jobs", value: "POSTED", link: "posted" },
              { label: "Onhold Jobs", value: "ONHOLD", link: "onhold" },
              { label: "Expired Jobs", value: "EXPIRED", link: "expired" },
            ].map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                component={Link}
                to={`/admin/job?active=${tab.link}`}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  px: 2.5,
                  ml: 1.5,
                  minHeight: 40,
                  borderRadius: 2,
                  color: "#1e293b",
                  transition: "all 0.2s ease",

                  "&.Mui-selected": {
                    color: "#ffff !important",
                    backgroundColor: "#235a7f",
                    fontWeight: 700,
                    // boxShadow: "inset 0 -2px 0 #2d6d96",
                  },

                  "&:hover": {
                    backgroundColor: "#2d6d96",
                    color: "#fff !important",
                  },
                }}
              />
            ))}
          </Tabs>
        </Box>
      </Paper>

      {/* ---------- TABLE SECTION ---------- */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid #e2ebf5",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#235a7f" }}>
            {activeTab.charAt(0) + activeTab.slice(1).toLowerCase()} Jobs
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer
          sx={{
            borderRadius: 2,
            border: "1px solid #eef2f7",
            overflowX: "auto",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "#F8FAFC",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#475569",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Vendor ID</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Owner Name</TableCell>
                <TableCell>Job Posted</TableCell>
                <TableCell>Active Job</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loader ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : Array.isArray(JobDetails) && JobDetails.length > 0 ? (
                JobDetails.map((item, i) => (
                  <TableRow
                    hover
                    key={i}
                    sx={{
                      "& td": {
                        fontSize: 13.5,
                        color: "#334155",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.vendorEmpId}
                    </TableCell>

                    <TableCell
                      sx={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.companyName}
                    </TableCell>

                    <TableCell
                      sx={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.vendorFullName || "N/A"}
                    </TableCell>

                    <TableCell>{item?.postedJob}</TableCell>
                    <TableCell>{item?.activeJob}</TableCell>

                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            location.pathname.includes("admin")
                              ? `/admin/job/vendor_jobs?vendorId=${item?.vendorId}`
                              : "/schedule-interview",
                          )
                        }
                        sx={{
                          backgroundColor: "#E6F0F8",
                          "&:hover": { backgroundColor: "#D6E7F5" },
                        }}
                      >
                        <VisibilityIcon
                          sx={{ fontSize: 18, color: "#286A94" }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "#64748b" }}
                  >
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ---------- PAGINATION ---------- */}
        {JobDetails?.length >= 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              totalPages={totalPages}
              currentPage={pageNumber}
              onPageChange={(newPage) => setPageNumber(newPage)}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default JobPage;
